/* N.B.
 * `@segment/consent-manager`'s commonjs build is broken until version 4.0.1:
 * https://github.com/segmentio/consent-manager/blob/master/CHANGELOG.md#401oct-10-2019
 * As a result, we have these hacky async imports that we then pass around.
 * They can be normal imports again if we upgrade.
 * */
import inEU from '@segment/in-eu';
import React from 'react';

import CookieConsentManager from 'analytics/CookieConsentManager';
import {ConsentPreferences, ConsentState, DestinationId} from 'analytics/Destinations';
import NonSegmentConsentManagerBuilder from 'analytics/NonSegmentConsentManagerBuilder';
import BenchlingConfig from 'BenchlingConfig';

export interface SegmentDestination {
  category: string;
  description: string;
  id: string;
  name: string;
  website: string;
}

export interface SegmentConsentPreferences {
  [destinationId: string]: boolean;
}

export interface SegmentConsentProps {
  destinations: Array<SegmentDestination>;
  preferences: ConsentPreferences;
  isConsentRequired: boolean;
  saveConsent: (newPreferences: ConsentPreferences) => void;
}

/**
 * Use this instance's hostname rather than the default of .benchling.com, since we want the
 * prefs to be distinct per instance. For example, if some switched between two instances and
 * they have different integrations enabled, then interacting with the modal on one might
 * clear settings for the other.
 */
function getCookieDomain(): string {
  return window.location.hostname;
}

/**
 * Google Tag Manager has a hard dependency on Google Analytics, so if the user opted out of Google
 * Analytics, automatically opt them out of Google Tag Manager as well.
 */
function reconcileSegmentPreferences(
  segmentPreferences: SegmentConsentPreferences
): SegmentConsentPreferences {
  if (
    segmentPreferences[DestinationId.GOOGLE_TAG_MANAGER] &&
    !segmentPreferences[DestinationId.GOOGLE_ANALYTICS]
  ) {
    segmentPreferences = {...segmentPreferences, [DestinationId.GOOGLE_TAG_MANAGER]: false};
  }
  return segmentPreferences;
}

/**
 * Transform our own preferences format to Segment's. Our preferences format can have destinations
 * not supported by Segment, and allows values to be unset.
 */
function mapCustomPreferences({
  destinations,
  preferences,
}: {
  destinations: Array<SegmentDestination>;
  preferences: ConsentPreferences;
}): {destinationPreferences: SegmentConsentPreferences; customPreferences: ConsentPreferences} {
  let destinationPreferences: SegmentConsentPreferences = {};
  for (const destination of destinations) {
    // Explicit disgree should be respected, and every other state (explicit agree, implicit agree,
    // never interacted with the modal because we don't need consent) counts as agreement.
    destinationPreferences[destination.id] = preferences[destination.id] !== ConsentState.EXPLICIT_DISAGREE;
  }
  destinationPreferences = reconcileSegmentPreferences(destinationPreferences);
  return {destinationPreferences, customPreferences: preferences};
}

/**
 * Users might have invalid cookies stored from previous bugs, so fix them and save back if so.
 */
function repairSavedPreferences({
  loadPreferences,
  savePreferences,
}: {
  loadPreferences: () => {
    customPreferences: ConsentPreferences;
    destinationPreferences: SegmentConsentPreferences;
  };
  savePreferences: (preferences: object) => void;
}): void {
  const {destinationPreferences, customPreferences} = loadPreferences();
  if (!destinationPreferences) {
    return;
  }
  const fixedDestinationPreferences = reconcileSegmentPreferences(destinationPreferences);
  if (fixedDestinationPreferences !== destinationPreferences) {
    savePreferences({
      destinationPreferences: fixedDestinationPreferences,
      customPreferences,
      cookieDomain: getCookieDomain(),
    });
  }
}

/**
 * Segment might not be accessible, either because we've disabled it for the deploy or because the
 * user is using adblock and the component will crash on mount due to the blocked destinations
 * call. Test early for those cases, and render a fallback wrapper if we detect it.
 */
async function testSegmentAccessible(
  fetchDestinationsFn: (destinations: Array<string>) => Promise<void>
): Promise<boolean> {
  if (!BenchlingConfig.ENABLE_SEGMENT_ANALYTICS_JS) {
    return false;
  }
  try {
    await fetchDestinationsFn([BenchlingConfig.SEGMENT_WRITE_KEY]);
    return true;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    return false;
  }
}

interface State {
  isReady: boolean;
  BuilderComponent:
    | typeof NonSegmentConsentManagerBuilder
    | React.ComponentClass<{
        writeKey: string;
        mapCustomPreferences: typeof mapCustomPreferences;
      }>;
  loadPreferences?: () => {
    customPreferences?: ConsentPreferences;
    destinationPreferences: SegmentConsentPreferences;
  };
  savePreferences?: (preferences: object) => void;
}

/**
 * Wrapper component to integrate the consent manager with the Segment API. This should be mounted
 * exactly once per page, at the top level, and does not render any DOM elements directly. Instead,
 * it opens a modal that interfaces with the ConsentManagerBuilder component.
 */
export default class CookieConsentManagerContainer extends React.Component<{}, State> {
  _isMounted = false;

  state: State = {
    isReady: false,
    BuilderComponent: NonSegmentConsentManagerBuilder,
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    const {loadPreferences, savePreferences} = await import(
      // @ts-expect-error
      '@segment/consent-manager/esm/consent-manager-builder/preferences'
    );
    const fetchSegmentDestinations = await import(
      // @ts-expect-error
      '@segment/consent-manager/esm/consent-manager-builder/fetch-destinations'
    );
    // @ts-expect-error
    const ConsentManagerBuilder = await import('@segment/consent-manager/esm/consent-manager-builder');
    repairSavedPreferences({loadPreferences, savePreferences});

    const isSegmentAccessible = await testSegmentAccessible(fetchSegmentDestinations);
    // Note that we never expect this component to unmount, but we want to be safe in case it does.
    if (this._isMounted) {
      this.setState({
        isReady: true,
        BuilderComponent: isSegmentAccessible ? ConsentManagerBuilder : NonSegmentConsentManagerBuilder,
        loadPreferences,
        savePreferences,
      }); // Should be fine for async update.
    }
  }

  componentWillUnmount(): void {
    // It's ok for the consent manager to unmount if a browser extension is causing it. See discussion in
    // BNCH-8642 for more details.
    this._isMounted = false;
  }

  render(): JSX.Element | null {
    const {isReady, BuilderComponent, loadPreferences, savePreferences} = this.state;

    // Skip all analytics for legal pages, since the user needs to be able to view the privacy
    // policy as part of agreeing.
    if (window._IS_LEGAL_PAGE || window._IS_INTERNAL_ADMIN_PAGE) {
      return null;
    }

    if (!isReady) {
      return null;
    }

    return (
      <BuilderComponent
        // loadPreferences and savePreferences will be set if isReady is true, but ts can't figure that out
        loadPreferences={loadPreferences!}
        savePreferences={savePreferences!}
        writeKey={BenchlingConfig.SEGMENT_WRITE_KEY}
        // Allow specifying ?require-consent-manager in the URL to pretend that we're in the EU. We
        // need to use the URL and not the hash since our routing will modify the hash.
        shouldRequireConsent={() => window.location.search.includes('require-consent-manager') || inEU()}
        mapCustomPreferences={mapCustomPreferences}
        cookieDomain={getCookieDomain()}
      >
        {(props: SegmentConsentProps) => <CookieConsentManager {...props} />}
      </BuilderComponent>
    );
  }
}
